/**
 * V52.12.2: 좌석배치도 캔버스 컴포넌트
 *
 * React Flow 기반 캔버스로 오브젝트를 표시하고
 * 드래그/선택 기능을 제공합니다.
 *
 * V52.12.2 핵심 변경사항:
 * - hover 기반(onNodeMouseEnter/Leave) 제거 → mousedown 기반으로 변경
 * - onNodeDragStart → panOnDrag=false (노드 드래그 시작 시 패닝 차단)
 * - onNodeDragStop/onMoveEnd → panOnDrag=[0] 복구 (드래그 종료 시)
 * - 가드 함수로 중복 setState 방지 (Maximum update depth 방지)
 *
 * V52.12 변경사항:
 * - 다중 선택(박스 선택) → Shift+드래그로 변경 (selectionKeyCode="Shift")
 *
 * V52.11 변경사항:
 * - handleNodesChange에서 'select' 타입 변경 필터링 → 선택 시 setNodes 호출 안 됨
 * - dragStop에서 isDraggingRef를 requestAnimationFrame으로 지연 → useEffect 스킵 보장
 *
 * V52.10 변경사항:
 * - nodes 구조와 UI 상태(선택, 중복경고) 분리
 * - derivedNodes deps에서 selectedObjectId, selectedObjectIds, duplicateSeatCodes 제거
 * - node.data에서 isSelected, isEditMode, isDuplicate 제거
 * - SeatMapContext로 UI 상태 제공 → SeatMapObjectNode에서 참조
 * - 클릭/선택 시 nodes 재생성 없음 → 무한 루프 근본 제거
 *
 * @module pages/organization/components/seatMap/SeatMapCanvas
 */

import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import {
  ReactFlow,
  Node,
  Background,
  BackgroundVariant,
  NodeTypes,
  ReactFlowInstance,
  MiniMap,
  Controls,
  SelectionMode,
  OnSelectionChangeParams,
  NodeChange,
  applyNodeChanges,
  useViewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { SeatMapObject, snapToGrid } from '../../../../shared/types/seatMap';
import { SeatMapObjectNode } from './SeatMapObjectNode';
import { SeatMapProvider } from './SeatMapContext';

// ============================================
// 타입 정의
// ============================================

interface SeatMapCanvasProps {
  /** 오브젝트 배열 */
  objects: SeatMapObject[];

  /** 캔버스 너비 */
  canvasWidth: number;

  /** 캔버스 높이 */
  canvasHeight: number;

  /** 그리드 크기 */
  gridSize: number;

  /** 편집 모드 여부 */
  isEditMode: boolean;

  /** 선택된 오브젝트 ID (단일) - UI 상태, nodes에 반영 안 됨 */
  selectedObjectId: string | null;

  /** V52.3: 선택된 오브젝트 ID 배열 (다중 선택) - UI 상태, nodes에 반영 안 됨 */
  selectedObjectIds: string[];

  /** 오브젝트 선택 핸들러 (단일) */
  onSelectObject: (objectId: string | null) => void;

  /** V52.3: 다중 선택 변경 핸들러 */
  onSelectionChange: (objectIds: string[]) => void;

  /** 오브젝트 업데이트 핸들러 */
  onUpdateObject: (objectId: string, updates: Partial<SeatMapObject>) => void;

  /** V52.3: 다중 오브젝트 업데이트 핸들러 (일괄 이동) */
  onUpdateObjects: (updates: Array<{ id: string; updates: Partial<SeatMapObject> }>) => void;

  /** V52.3: 중복 좌석 코드 Set (경고 표시용) - Context로 전달 */
  duplicateSeatCodes: Set<string>;
}

// ============================================
// 커스텀 노드 타입 등록
// ============================================

const nodeTypes: NodeTypes = {
  seatMapObject: SeatMapObjectNode,
};

// ============================================
// V52.8.2: 캔버스 경계 시각화 오버레이 (UX 강화)
// V55.19: will-change + transform으로 GPU 가속, ResizeObserver 루프 방지
// ============================================

const CanvasBoundaryOverlay = React.memo(function CanvasBoundaryOverlay({
  canvasWidth,
  canvasHeight,
}: {
  canvasWidth: number;
  canvasHeight: number;
}) {
  const { x, y, zoom } = useViewport();

  const screenWidth = canvasWidth * zoom;
  const screenHeight = canvasHeight * zoom;

  const markerLength = 20;
  const markerThickness = 3;

  // V55.19: transform 기반 위치 지정으로 레이아웃 재계산 방지
  // will-change: transform으로 GPU 레이어 승격
  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    pointerEvents: 'none',
    willChange: 'transform',
  };

  return (
    <>
      {/* 캔버스 경계 + 외부 마스크 */}
      {/* V54.4: 불투명 마스크(#f8fafc)로 캔버스 바깥 완전히 숨김 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x}px, ${y}px)`,
          width: screenWidth,
          height: screenHeight,
          boxShadow: '0 0 0 9999px #f8fafc',
          border: '2px dashed #3b82f6',
          boxSizing: 'border-box',
          zIndex: 5,
        }}
      />

      {/* V52.8.2: 좌측 경계 그라데이션 힌트 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x}px, ${y}px)`,
          width: Math.min(24, screenWidth * 0.05),
          height: screenHeight,
          background: 'linear-gradient(to right, rgba(59, 130, 246, 0.08), transparent)',
          zIndex: 4,
        }}
      />

      {/* V52.8.2: 상단 경계 그라데이션 힌트 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x}px, ${y}px)`,
          width: screenWidth,
          height: Math.min(24, screenHeight * 0.05),
          background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.08), transparent)',
          zIndex: 4,
        }}
      />

      {/* V52.8.2: (0,0) L자 코너 마커 - 가로 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x - markerThickness}px, ${y - markerThickness}px)`,
          width: markerLength,
          height: markerThickness,
          background: '#2563eb',
          zIndex: 6,
        }}
      />

      {/* V52.8.2: (0,0) L자 코너 마커 - 세로 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x - markerThickness}px, ${y - markerThickness}px)`,
          width: markerThickness,
          height: markerLength,
          background: '#2563eb',
          zIndex: 6,
        }}
      />

      {/* V52.8.2: START (0,0) 배지 */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x + 8}px, ${y - 24}px)`,
          padding: '2px 6px',
          background: '#2563eb',
          color: 'white',
          fontSize: 10,
          fontWeight: 700,
          borderRadius: 3,
          zIndex: 6,
          whiteSpace: 'nowrap',
        }}
      >
        START (0,0)
      </div>

      {/* 캔버스 크기 라벨 (우상단) */}
      <div
        style={{
          ...baseStyle,
          transform: `translate(${x + screenWidth - 80}px, ${y - 20}px)`,
          padding: '2px 6px',
          background: 'rgba(255, 255, 255, 0.9)',
          color: '#64748b',
          fontSize: 10,
          fontWeight: 500,
          borderRadius: 3,
          zIndex: 6,
          whiteSpace: 'nowrap',
        }}
      >
        {canvasWidth} × {canvasHeight}
      </div>
    </>
  );
});

// ============================================
// V52.8.2: 경계 안내 토스트
// ============================================

interface BoundaryToastProps {
  message: string;
  visible: boolean;
}

function BoundaryToast({ message, visible }: BoundaryToastProps) {
  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 16px',
        background: 'rgba(37, 99, 235, 0.95)',
        color: 'white',
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {message}
    </div>
  );
}

// ============================================
// 캔버스 컴포넌트
// ============================================

export function SeatMapCanvas({
  objects,
  canvasWidth,
  canvasHeight,
  gridSize,
  isEditMode,
  selectedObjectId,
  selectedObjectIds,
  onSelectObject,
  onSelectionChange,
  onUpdateObject,
  onUpdateObjects,
  duplicateSeatCodes,
}: SeatMapCanvasProps) {
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  // V52.5: 드래그 중 상태 추적 (useEffect에서 setNodes 호출 방지용)
  const isDraggingRef = useRef(false);

  // V54.9: 오브젝트 업데이트 중 상태 추적 (선택 해제 방지용)
  // - nodes 동기화 중에 onSelectionChange가 빈 배열로 트리거되는 것을 방지
  const isUpdatingRef = useRef(false);

  // V52.8.2: 경계 토스트 상태
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastCooldownRef = useRef(0);

  // V55.19: wrapper ref (Pointer Capture 제거됨, 레이아웃 안정성용으로만 유지)
  const reactFlowWrapperRef = useRef<HTMLDivElement | null>(null);

  // V52.12.2: mousedown 기반 panOnDrag 제어
  // - 기본값 [0]: 빈 공간에서 좌클릭 드래그 → 패닝
  // - 노드 드래그 시작 시 false: 패닝 차단, 노드 드래그 우선
  // - 드래그 종료 시 [0] 복구
  const [panOnDragState, setPanOnDragState] = useState<boolean | number[]>([0]);

  // V52.12.2: 가드 함수 - 같은 값이면 setState 안 함 (Maximum update depth 방지)
  const setPanOnDragGuarded = useCallback((next: boolean | number[]) => {
    setPanOnDragState((prev) => {
      // boolean vs boolean
      if (typeof prev === 'boolean' && typeof next === 'boolean') {
        return prev === next ? prev : next;
      }
      // array vs array (둘 다 [0]인 경우)
      if (Array.isArray(prev) && Array.isArray(next)) {
        if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
          return prev; // 같으면 이전 참조 유지
        }
      }
      // 타입이 다르면 무조건 변경
      return next;
    });
  }, []);

  // V52.8.2: 경계 토스트 표시 함수 (3초 쿨다운)
  const showBoundaryToast = useCallback((message: string) => {
    const now = Date.now();
    if (now - toastCooldownRef.current < 3000) return;

    toastCooldownRef.current = now;
    setToastMessage(message);
    setToastVisible(true);

    setTimeout(() => {
      setToastVisible(false);
    }, 1500);
  }, []);

  // V52.2: Zone ID → zoneCode 맵 생성
  const zoneCodeMap = useMemo(() => {
    const map = new Map<string, string>();
    objects.forEach((obj) => {
      if (obj.type === 'zone' && obj.zoneCode) {
        map.set(obj.id, obj.zoneCode);
      }
    });
    return map;
  }, [objects]);

  // ============================================
  // V52.10: 오브젝트 → React Flow 노드 변환 (SSoT: objects)
  // 핵심: UI 상태(선택, 중복) 제외 → 클릭/선택 시 재생성 안 됨
  // ============================================
  const derivedNodes = useMemo<Node[]>(() => {
    return objects.map((obj) => {
      // V52.2: 좌석의 경우 zoneId로 zoneCode 조회
      const resolvedZoneCode = obj.type === 'seat' && obj.zoneId
        ? zoneCodeMap.get(obj.zoneId)
        : undefined;

      return {
        id: obj.id,
        type: 'seatMapObject',
        position: { x: obj.x, y: obj.y },
        // V55.22: floor(-1000) < zone(-10) < 기타 오브젝트(0)
        zIndex: obj.type === 'floor' ? -1000 : obj.type === 'zone' ? -10 : (obj.zIndex ?? 0),
        // V52.10: data에는 구조 정보만 (UI 상태 제거됨)
        data: {
          ...obj,
          resolvedZoneCode,
        },
        draggable: isEditMode,
        selectable: isEditMode,
        // V52.10: selected 제거 - React Flow 내부 상태로 관리
      };
    });
  // V52.10: deps에서 UI 상태 제거 - 클릭/선택으로 재계산 안 됨
  }, [objects, isEditMode, zoneCodeMap]);

  // V52.10: 내부 nodes 상태 (드래그 중 시각적 위치 관리)
  const [nodes, setNodes] = useState<Node[]>(derivedNodes);

  // V52.10: objects(SSoT) 변경 시 노드 동기화
  // - derivedNodes가 objects 변경으로만 재계산되므로, 클릭/선택 시 실행 안 됨
  // - isDraggingRef가 true이면 건너뜀 (드래그 중 덮어쓰기 방지)
  // V54.8: 선택 상태 보존 - 속성 패널에서 +/- 버튼 클릭 시 패널 닫힘 방지
  // V54.9: isUpdatingRef로 onSelectionChange 트리거 방지
  useEffect(() => {
    if (isDraggingRef.current) {
      return;
    }

    // V54.9: 업데이트 시작 - onSelectionChange에서 선택 해제 방지
    isUpdatingRef.current = true;

    // V54.8: 기존 선택 상태 보존
    // - setNodes로 전체 교체 시 React Flow의 내부 selected 상태가 손실됨
    // - 기존 nodes의 selected 상태를 새 derivedNodes에 반영하여 선택 유지
    setNodes((prevNodes) => {
      const selectedIds = new Set(prevNodes.filter((n) => n.selected).map((n) => n.id));
      if (selectedIds.size === 0) {
        // 선택된 노드 없으면 그냥 교체
        return derivedNodes;
      }
      // 선택 상태 보존
      return derivedNodes.map((node) => ({
        ...node,
        selected: selectedIds.has(node.id),
      }));
    });

    // V54.9: 다음 프레임에서 업데이트 완료 처리
    // - React Flow의 onSelectionChange가 트리거된 후 reset
    requestAnimationFrame(() => {
      isUpdatingRef.current = false;
    });
  }, [derivedNodes]);

  // V52.11: onNodesChange 핸들러 - position 변경만 처리
  // 핵심: 'select' 타입 변경은 무시 → 선택으로 인한 setNodes 호출 방지
  const handleNodesChange = useCallback((changes: NodeChange[]) => {
    // V52.11: select 타입 변경 필터링 (선택은 React Flow 내부에서 처리)
    const positionChanges = changes.filter((c) => c.type !== 'select');

    if (positionChanges.length === 0) {
      // select만 있으면 setNodes 호출 안 함
      return;
    }

    setNodes((nds) => applyNodeChanges(positionChanges, nds));
  }, []);

  // 노드 클릭 핸들러
  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (isEditMode) {
        onSelectObject(node.id);
      }
    },
    [isEditMode, onSelectObject]
  );

  // 캔버스 클릭 핸들러 (선택 해제)
  const handlePaneClick = useCallback(() => {
    if (isEditMode) {
      onSelectObject(null);
    }
  }, [isEditMode, onSelectObject]);

  // V52.12.2: 노드 드래그 시작 핸들러
  // - isDraggingRef 설정 (V52.5)
  // - panOnDrag=false로 설정 (패닝 차단, 노드 드래그 우선)
  // V55.19: 디버그 로깅 제거 (ResizeObserver 루프 방지)
  const handleNodeDragStart = useCallback(
    (_: React.MouseEvent, _node: Node) => {
      isDraggingRef.current = true;
      setPanOnDragGuarded(false);
    },
    [setPanOnDragGuarded]
  );

  // V52.12.2: 다중 선택 드래그 시작 핸들러
  const handleSelectionDragStart = useCallback(() => {
    isDraggingRef.current = true;
    setPanOnDragGuarded(false); // V52.12.2: 다중 선택 드래그 중 패닝 차단
  }, [setPanOnDragGuarded]);

  // 노드 드래그 종료 핸들러 (단일)
  const handleNodeDragStop = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (!isEditMode) {
        isDraggingRef.current = false;
        return;
      }

      // 그리드 스냅 적용
      const snappedX = snapToGrid(node.position.x, gridSize);
      const snappedY = snapToGrid(node.position.y, gridSize);

      // 캔버스 범위 제한
      const obj = objects.find((o) => o.id === node.id);
      if (!obj) {
        isDraggingRef.current = false;
        return;
      }

      const safeCanvasWidth = Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : 1920;
      const safeCanvasHeight = Number.isFinite(canvasHeight) && canvasHeight > 0 ? canvasHeight : 1080;
      const safeObjWidth = Number.isFinite(obj.width) && obj.width > 0 ? obj.width : 60;
      const safeObjHeight = Number.isFinite(obj.height) && obj.height > 0 ? obj.height : 60;

      const maxX = Math.max(0, safeCanvasWidth - safeObjWidth);
      const maxY = Math.max(0, safeCanvasHeight - safeObjHeight);
      const clampedX = Math.min(Math.max(0, snappedX), maxX);
      const clampedY = Math.min(Math.max(0, snappedY), maxY);

      // V52.8.2: 경계에 닿았을 때 토스트 표시
      const hitLeftTop = clampedX === 0 || clampedY === 0;
      const hitRightBottom = clampedX === maxX || clampedY === maxY;
      if (hitLeftTop || hitRightBottom) {
        showBoundaryToast(`캔버스 경계입니다 (0 ~ ${safeCanvasWidth}×${safeCanvasHeight})`);
      }

      // V52.5.1: 드롭 즉시 nodes를 clamped 좌표로 업데이트
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, position: { x: clampedX, y: clampedY } }
            : n
        )
      );

      // objects도 업데이트 (SSoT 동기화)
      onUpdateObject(node.id, {
        x: clampedX,
        y: clampedY,
      });

      // V52.11: isDraggingRef를 다음 프레임에서 false로 설정
      requestAnimationFrame(() => {
        isDraggingRef.current = false;
        setPanOnDragGuarded([0]);
      });
    },
    [isEditMode, gridSize, canvasWidth, canvasHeight, objects, onUpdateObject, showBoundaryToast, setPanOnDragGuarded]
  );

  // V52.3: 다중 노드 드래그 종료 핸들러
  const handleSelectionDragStop = useCallback(
    (_: React.MouseEvent, draggedNodes: Node[]) => {
      if (!isEditMode || draggedNodes.length === 0) {
        isDraggingRef.current = false;
        return;
      }

      const safeCanvasWidth = Number.isFinite(canvasWidth) && canvasWidth > 0 ? canvasWidth : 1920;
      const safeCanvasHeight = Number.isFinite(canvasHeight) && canvasHeight > 0 ? canvasHeight : 1080;

      const updates = draggedNodes.map((node) => {
        const obj = objects.find((o) => o.id === node.id);
        if (!obj) return null;

        const snappedX = snapToGrid(node.position.x, gridSize);
        const snappedY = snapToGrid(node.position.y, gridSize);

        const safeObjWidth = Number.isFinite(obj.width) && obj.width > 0 ? obj.width : 60;
        const safeObjHeight = Number.isFinite(obj.height) && obj.height > 0 ? obj.height : 60;

        const maxX = Math.max(0, safeCanvasWidth - safeObjWidth);
        const maxY = Math.max(0, safeCanvasHeight - safeObjHeight);
        const clampedX = Math.min(Math.max(0, snappedX), maxX);
        const clampedY = Math.min(Math.max(0, snappedY), maxY);

        return {
          id: node.id,
          clampedX,
          clampedY,
          hitBoundary: clampedX === 0 || clampedY === 0 || clampedX === maxX || clampedY === maxY,
        };
      }).filter((u): u is { id: string; clampedX: number; clampedY: number; hitBoundary: boolean } => u !== null);

      // V52.8.2: 경계에 닿은 노드가 있으면 토스트 표시
      if (updates.some((u) => u.hitBoundary)) {
        showBoundaryToast(`캔버스 경계입니다 (0 ~ ${safeCanvasWidth}×${safeCanvasHeight})`);
      }

      if (updates.length > 0) {
        // 드롭 즉시 nodes를 clamped 좌표로 업데이트
        setNodes((nds) =>
          nds.map((n) => {
            const update = updates.find((u) => u.id === n.id);
            if (update) {
              return { ...n, position: { x: update.clampedX, y: update.clampedY } };
            }
            return n;
          })
        );

        // objects도 업데이트 (SSoT 동기화)
        onUpdateObjects(
          updates.map((u) => ({
            id: u.id,
            updates: { x: u.clampedX, y: u.clampedY },
          }))
        );

        // V52.11 + V52.12.2: isDraggingRef와 panOnDrag를 다음 프레임에서 복구
        requestAnimationFrame(() => {
          isDraggingRef.current = false;
          setPanOnDragGuarded([0]); // V52.12.2: 패닝 재활성화
        });
      } else {
        // V52.11 + V52.12.2: 업데이트 없을 때도 다음 프레임에서 복구
        requestAnimationFrame(() => {
          isDraggingRef.current = false;
          setPanOnDragGuarded([0]); // V52.12.2: 패닝 재활성화
        });
      }
    },
    [isEditMode, gridSize, canvasWidth, canvasHeight, objects, onUpdateObjects, showBoundaryToast, setPanOnDragGuarded]
  );

  // V52.3: 선택 변경 핸들러
  // V54.9: isUpdatingRef가 true이면 선택 해제 무시 (패널 +/- 클릭 시 닫힘 방지)
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: OnSelectionChangeParams) => {
      const ids = selectedNodes.map((n) => n.id);

      // V54.9: 오브젝트 업데이트 중에는 빈 선택 무시
      // - setNodes 후 React Flow가 onSelectionChange를 빈 배열로 트리거하는 것을 방지
      if (ids.length === 0 && isUpdatingRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[V54.9] handleSelectionChange SKIPPED (isUpdating=true)');
        }
        return;
      }

      onSelectionChange(ids);

      if (ids.length === 1) {
        onSelectObject(ids[0]);
      } else if (ids.length === 0) {
        onSelectObject(null);
      }
    },
    [onSelectionChange, onSelectObject]
  );

  // ReactFlow 초기화
  // V54.9: fitView는 onInit에서만 한 번 호출 (ResizeObserver loop 방지)
  // - fitView prop 제거: 매 렌더마다 뷰포트 조정 → resize observer 루프 유발
  // - 초기 진입 시에만 fitView, 이후 +/-는 현재 뷰 유지
  const handleInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
    // V54.9: 초기 로드 시에만 fitView 호출 (rAF로 루프 방지)
    requestAnimationFrame(() => {
      instance.fitView({ padding: isEditMode ? 0.1 : 0.05 });
    });
  }, [isEditMode]);

  // V52.12.2: 캔버스 이동(패닝/줌) 종료 시 panOnDrag 복구
  // - 패닝 종료 후 다시 [0]으로 설정하여 다음 드래그에 대비
  const handleMoveEnd = useCallback(() => {
    setPanOnDragGuarded([0]);
  }, [setPanOnDragGuarded]);

  // V55.19: key 리마운트 제거 - ResizeObserver 루프 유발 원인
  // canvasWidth/canvasHeight 변경은 리마운트 없이 ReactFlow 내부에서 처리됨
  // nodeExtent/translateExtent도 제거하여 드래그 중 완전 자유 이동

  // V54.4: 편집/조회 모두 캔버스 영역만 보이도록 클리핑
  // - 편집 모드: 그리드 + 경계 오버레이 표시, 바깥은 불투명 마스크로 숨김
  // - 조회 모드: 캔버스 영역만 보이게 클리핑, 배경 없음
  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    background: isEditMode ? '#f8fafc' : 'white',
    overflow: 'hidden',  // V54.4: 편집 모드에서도 overflow hidden 적용
  };

  return (
    <div
      ref={reactFlowWrapperRef}
      style={containerStyle}
    >
      {/* V52.10: SeatMapProvider로 UI 상태 제공 */}
      <SeatMapProvider isEditMode={isEditMode} duplicateSeatCodes={duplicateSeatCodes}>
        {/* V55.19: key 리마운트 제거 - ResizeObserver 루프 방지 */}
        {/* nodeExtent/translateExtent 없음 → 드래그 중 완전 자유, 드롭 시 clamp */}
        <ReactFlow
          nodes={nodes}
          edges={[]}
          onNodesChange={handleNodesChange}
          onNodeClick={handleNodeClick}
          onNodeDragStart={handleNodeDragStart}
          onNodeDragStop={handleNodeDragStop}
          onSelectionDragStart={handleSelectionDragStart}
          onSelectionDragStop={handleSelectionDragStop}
          onSelectionChange={handleSelectionChange}
          onPaneClick={handlePaneClick}
          onInit={handleInit}
          // V52.12.2: 캔버스 이동 종료 시 panOnDrag 복구
          onMoveEnd={handleMoveEnd}
          nodeTypes={nodeTypes}
          snapToGrid={isEditMode}
          snapGrid={[gridSize, gridSize]}
          // V54.9: fitView prop 제거 - onInit에서만 호출 (ResizeObserver loop 방지)
          minZoom={0.1}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          proOptions={{ hideAttribution: true }}
          // V54.3: View 모드는 흰 배경, 편집 모드는 회색 배경
          style={{ background: isEditMode ? '#f0f4f8' : 'white' }}
          // V52.12.2: mousedown 기반 panOnDrag 제어
          // - 기본값 [0]: 빈 공간에서 좌클릭 드래그 → 패닝
          // - 노드 드래그 시작 시 false: 패닝 차단
          // - 드래그 종료 시 [0] 복구
          panOnDrag={panOnDragState}
          selectionOnDrag={false}
          selectionKeyCode={isEditMode ? 'Shift' : null}
          selectionMode={SelectionMode.Partial}
          selectNodesOnDrag={isEditMode}
          deleteKeyCode={isEditMode ? 'Delete' : null}
          // V55.17: 모든 extent 제거 → 드래그 중 완전 자유 이동
          // 드롭 시 handleNodeDragStop/handleSelectionDragStop에서 오브젝트 크기 고려 clamp만 사용
          // nodeExtent={undefined}
          // translateExtent={undefined}
        >
          {/* V54.3: 편집 모드에서만 그리드 배경 표시 */}
          {isEditMode && (
            <Background
              variant={BackgroundVariant.Dots}
              gap={gridSize}
              size={1}
              color="#94a3b8"
            />
          )}
          {/* V54.3: 편집 모드에서만 MiniMap 표시 */}
          {isEditMode && (
            <MiniMap
              nodeColor={(node) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const data = node.data as any;
                if (data?.type === 'zone') return '#94a3b8';
                if (data?.type === 'seat') return data?.employeeId ? '#3b82f6' : '#93c5fd';
                return '#64748b';
              }}
              maskColor="rgba(0, 0, 0, 0.1)"
              style={{ background: '#f8fafc' }}
            />
          )}
          {/* V54.3: 줌 컨트롤 - 조회 모드에서도 표시 (휠 줌 + fitView 버튼) */}
          <Controls
            showZoom
            showFitView
            showInteractive={false}
            position="bottom-left"
          />
          {/* V54.3: 편집 모드에서만 경계 오버레이 표시 */}
          {isEditMode && (
            <CanvasBoundaryOverlay
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
            />
          )}
          {isEditMode && (
            <BoundaryToast message={toastMessage} visible={toastVisible} />
          )}
        </ReactFlow>
      </SeatMapProvider>
    </div>
  );
}
